import { useCallback, useEffect, useState } from "react";
import { editorialCategories as editorialCategoriesService } from "../../../api/editorialCategories.service";

type EditorialCategoryOption = {
  value: number | string;
  label: string;
  group?: string;
};

/**
 * Manages editorial category options (practice areas) for a specific placement region.
 * Categories are fetched when the region changes.
 */
export function useEditorialCategories(
  initialRegionId: string | number | null | undefined,
  initialPracticeGroupIds?: (number | string)[],
) {
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(
    initialRegionId ? String(initialRegionId) : null,
  );
  const [editorialCategoryOptions, setEditorialCategoryOptions] = useState<
    EditorialCategoryOption[]
  >([]);
  const [practiceGroupIdMap, setPracticeGroupIdMap] = useState<Map<string, number | string>>(
    new Map(),
  );
  const [selectedPracticeGroups, setSelectedPracticeGroups] = useState<(number | string)[]>(
    initialPracticeGroupIds || [],
  );
  const [isLoading, setIsLoading] = useState(false);

  const fetchCategories = useCallback(async (countryId: number) => {
    setIsLoading(true);
    try {
      const response = await editorialCategoriesService.getAll({
        countryId,
        type: "practice_area",
        limit: 100,
      });

      const options = response.editorialCategories.map(category => ({
        value: category.id,
        label: category.practiceArea ?? `Category ${category.id}`,
        group: category.practiceGroup || undefined,
      }));

      const groupMap = new Map<string, number | string>();
      for (const category of response.editorialCategories) {
        if (category.practiceGroup && category.practiceGroupId) {
          groupMap.set(category.practiceGroup, Number(category.practiceGroupId));
        }
      }

      setPracticeGroupIdMap(groupMap);
      setEditorialCategoryOptions(options);
    } catch (error) {
      console.error("Failed to fetch editorial categories:", error);
      setEditorialCategoryOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initialRegionId) {
      const countryId = Number(initialRegionId);
      setSelectedRegionId(String(initialRegionId));
      if (!Number.isNaN(countryId)) {
        fetchCategories(countryId);
      }
    }
  }, [initialRegionId, fetchCategories]);

  // Set initial practice groups from subscription metadata
  useEffect(() => {
    if (initialPracticeGroupIds && initialPracticeGroupIds.length > 0) {
      setSelectedPracticeGroups(initialPracticeGroupIds);
    }
  }, [initialPracticeGroupIds]);

  const handleRegionChange = useCallback(
    async (regionId: string) => {
      setSelectedRegionId(regionId);
      setSelectedPracticeGroups([]);
      const countryId = Number(regionId);
      if (Number.isNaN(countryId)) {
        setEditorialCategoryOptions([]);
        setPracticeGroupIdMap(new Map());
        return;
      }
      await fetchCategories(countryId);
    },
    [fetchCategories],
  );

  const handlePracticeGroupChange = useCallback((groups: (number | string)[]) => {
    setSelectedPracticeGroups(groups);
  }, []);

  return {
    selectedRegionId,
    editorialCategoryOptions,
    practiceGroupIdMap,
    selectedPracticeGroups,
    isLoading,
    handleRegionChange,
    handlePracticeGroupChange,
  };
}
